import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingApi } from './api';

const PLAN_LABELS = {
  trial: 'Free Trial',
  monthly: 'Monthly',
  yearly: 'Yearly',
  lifetime: 'Lifetime',
};

const STATUS_TONE = {
  active: 'live',
  past_due: 'paper',
  canceled: 'off',
};

function PlanButton({ plan, label, price, onBuy, busy }) {
  return (
    <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p className="stat-label">{label}</p>
      <p className="stat-value">{price}</p>
      <button className="primary-button" onClick={() => onBuy(plan)} disabled={busy}>
        {busy ? 'Redirecting…' : `Choose ${label}`}
      </button>
    </div>
  );
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);

  const checkoutResult = searchParams.get('checkout');

  useEffect(() => {
    billingApi.status().then(setStatus).catch((err) => setError(err.message));
  }, [checkoutResult]);

  const handleBuy = async (plan) => {
    setBusyPlan(plan);
    setError(null);
    try {
      const { url } = await billingApi.checkout(plan);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusyPlan(null);
    }
  };

  const isExpired = status?.planExpiresAt && new Date(status.planExpiresAt) < new Date();

  return (
    <div className="auth-screen">
      <div className="auth-card settings-card">
        <div className="auth-brand">QuantumTrade</div>
        <h1 className="auth-title">Billing</h1>

        {checkoutResult === 'success' && (
          <div className="success-box" style={{ marginBottom: 16 }}>
            Payment received — your plan will update within a few seconds once Stripe confirms it.
          </div>
        )}
        {checkoutResult === 'cancel' && (
          <div className="input-hint" style={{ marginBottom: 16 }}>
            Checkout was canceled — no charge was made.
          </div>
        )}
        {error && <div className="error-box" style={{ marginBottom: 16 }}>{error}</div>}

        <div className="settings-section" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
          <h2>Current plan</h2>
          {!status ? (
            <span className="skeleton-bar" style={{ width: '100%' }} />
          ) : (
            <div className="stats-grid">
              <div className="stat-card">
                <p className="stat-label">Plan</p>
                <p className="stat-value">{PLAN_LABELS[status.plan] || status.plan}</p>
              </div>
              <div className="stat-card">
                <p className="stat-label">Status</p>
                <span className={`status-pill-link ${STATUS_TONE[status.planStatus] || 'off'}`}>
                  <span className="status-dot" />
                  {isExpired ? 'expired' : status.planStatus}
                </span>
              </div>
              <div className="stat-card">
                <p className="stat-label">{status.plan === 'lifetime' ? 'Expires' : 'Renews / Expires'}</p>
                <p className="stat-value">
                  {status.planExpiresAt ? new Date(status.planExpiresAt).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="settings-section">
          <h2>Plans</h2>
          <div className="stats-grid">
            <PlanButton plan="monthly" label="Monthly" price="$20 / 30 days" onBuy={handleBuy} busy={busyPlan === 'monthly'} />
            <PlanButton plan="yearly" label="Yearly" price="$185 / year" onBuy={handleBuy} busy={busyPlan === 'yearly'} />
            <PlanButton plan="lifetime" label="Lifetime" price="$4,500 once" onBuy={handleBuy} busy={busyPlan === 'lifetime'} />
          </div>
          <p className="input-hint" style={{ marginTop: 12 }}>
            Monthly and Yearly renew automatically until canceled. Lifetime is a one-time payment.
          </p>
        </div>

        <button className="secondary-button" onClick={() => navigate('/')}>
          Back to dashboard
        </button>
      </div>
    </div>
  );
}
